/**
 * Builder Console (page builder, WS-3) — the bottom dock turned into a developer
 * console (the Appsmith model): you no longer EDIT here, you OBSERVE. Tabs:
 *
 *   - **Response** — the last query / JS run's result (wired in WS-5).
 *   - **Logs** — `console.log` output from a run (wired in WS-5).
 *   - **Linter** — compile errors across the page's JS Objects (live).
 *   - **Stato** — the page's `$page` + `$global` keys with their preview values.
 *   - **Riferimenti** — the read-only bindings explorer (`DataPanel`).
 *
 * In the builder there is no live runtime, so "Stato" shows the DECLARED keys
 * with their initial / preview values (it goes live in the portal). Resize /
 * collapse mirror the workflow editor's run-details dock.
 */
import { Ban, ChevronDown, ChevronUp, GripHorizontal } from "lucide-react";
import * as React from "react";

import type { LintIssue } from "@/lib/pageBuilder/lint";
import type { LogEntry } from "@/lib/pageBuilder/runLog";
import type { PageQuery, PageVariable } from "@/lib/pageTypes";

import { DataPanel } from "./DataPanel";

const MIN_HEIGHT = 200;
const MAX_HEIGHT = 640;
const COLLAPSED_HEIGHT = 38;

interface QueryPreview {
  rows: Record<string, unknown>[];
  total: number;
}

/** The last explicit run's result (WS-5) — a query preview OR a JS Object
 *  function test. Shown in the Console **Response** tab (the Appsmith model). */
export type ConsoleResponse =
  | { kind: "query"; label: string; time: string; ok: true; rows: Record<string, unknown>[]; total: number }
  | { kind: "js"; label: string; time: string; ok: true; display: string }
  | { kind: "query" | "js"; label: string; time: string; ok: false; error: string };

type ConsoleTab = "response" | "logs" | "linter" | "state" | "refs";

export interface BuilderConsoleProps {
  /** The page's queries — listed in the bindings explorer (read-only). */
  queries: PageQuery[];
  /** Per-query preview (rows + total), keyed by query name. */
  preview: Record<string, QueryPreview>;
  /** Declared page variables ($page) — listed in the explorer. */
  variables: PageVariable[];
  /** Implicit $page keys written by components (input/select_into). */
  stateKeys: string[];
  /** Lint issues across the whole page (JS Objects + action JS + bindings). */
  lintIssues: LintIssue[];
  /** Captured run logs (newest last); cleared via `onClearLogs`. */
  logs: LogEntry[];
  onClearLogs: () => void;
  /** The last explicit run's result (Response tab), or null before any run. */
  response: ConsoleResponse | null;
  /** Bumped on each run so the Console focuses the Response tab (Appsmith UX). */
  responseNonce: number;
  /** Current `$page` keys → preview values (Stato tab). */
  pageState: Record<string, unknown>;
  /** Current `$global` keys → preview values (Stato tab). */
  globalState: Record<string, unknown>;
  /** Bindings explorer context (DataPanel). */
  user: { id: string; email: string; full_name?: string | null } | null;
  role: { slug?: string; name?: string } | null;
  itemColumns?: string[];
  isOverlay?: boolean;
  inputKeys?: string[];
  onInsert?: (expr: string) => boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  height: number;
  onHeightChange: (h: number) => void;
}

export function BuilderConsole({
  queries,
  preview,
  variables,
  stateKeys,
  lintIssues,
  logs,
  onClearLogs,
  response,
  responseNonce,
  pageState,
  globalState,
  user,
  role,
  itemColumns,
  isOverlay,
  inputKeys,
  onInsert,
  open,
  onOpenChange,
  height,
  onHeightChange,
}: BuilderConsoleProps) {
  const [tab, setTab] = React.useState<ConsoleTab>("refs");

  // A new run (query preview / JS tester) FOCUSES the Response tab — the
  // Appsmith "run → see result" feel. Guarded on nonce>0 so the initial mount
  // (no run yet) keeps the default Riferimenti tab.
  React.useEffect(() => {
    if (responseNonce > 0) setTab("response");
  }, [responseNonce]);

  // Drag-to-resize (handle on the top edge) — mirrors RunDetailsPanel.
  const dragRef = React.useRef<{ startY: number; startH: number } | null>(null);
  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      const s = dragRef.current;
      if (!s) return;
      onHeightChange(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, s.startH + (s.startY - e.clientY))));
    }
    function onUp() {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [onHeightChange]);
  function startDrag(e: React.MouseEvent) {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: height };
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  }

  const effectiveHeight = open ? height : COLLAPSED_HEIGHT;
  const tabDefs: [ConsoleTab, string, number | null][] = [
    ["response", "Response", null],
    ["logs", "Logs", logs.length],
    ["linter", "Linter", lintIssues.length],
    ["state", "Stato", Object.keys(pageState).length + Object.keys(globalState).length],
    ["refs", "Riferimenti", null],
  ];

  // Linter labels per surface kind (the JS error is in a JS Object, an action's
  // JS, or a {{ }} binding — owner asked the linter cover all three).
  const KIND_LABEL: Record<LintIssue["kind"], string> = {
    "js-object": "JS Object",
    "action-js": "Azione JS",
    binding: "Binding",
  };

  return (
    <div
      className="flex shrink-0 flex-col border-t border-border bg-surface"
      style={{ height: effectiveHeight }}
    >
      {open && (
        <div
          onMouseDown={startDrag}
          className="group flex h-1.5 shrink-0 cursor-row-resize items-center justify-center bg-border/40 transition hover:bg-accent"
          title="Trascina per ridimensionare"
        >
          <GripHorizontal className="h-2.5 w-2.5 text-muted-foreground/40 group-hover:text-foreground" strokeWidth={2} />
        </div>
      )}

      {/* Header bar (ALWAYS visible): the console tabs + expand/collapse. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-1.5 text-[11px]">
        <div className="flex items-center rounded-md border border-border/60 p-0.5">
          {tabDefs.map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setTab(key);
                if (!open) onOpenChange(true);
              }}
              className={`rounded px-2 py-0.5 font-medium ${
                open && tab === key ? "bg-accent/15 text-accent" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
              {count !== null && count > 0 && (
                <span className={key === "linter" ? "ml-1 rounded bg-danger/15 px-1 text-danger" : "opacity-60"}>
                  {key === "linter" ? count : ` · ${count}`}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className="rounded p-0.5 text-muted-foreground hover:bg-surface hover:text-foreground"
          title={open ? "Comprimi" : "Espandi"}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} /> : <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {tab === "response" &&
            (response === null ? (
              <p className="text-[11px] text-muted-foreground">
                Esegui una query o una funzione JS per vederne il risultato qui.
              </p>
            ) : (
              <ResponseView response={response} />
            ))}

          {tab === "logs" &&
            (logs.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                Esegui una funzione JS («Prova funzione») o un'anteprima query: i{" "}
                <code className="rounded bg-muted px-1">console.log</code> e gli esiti compaiono qui.
              </p>
            ) : (
              <div className="flex flex-col gap-0.5">
                <div className="mb-1 flex justify-end">
                  <button
                    type="button"
                    onClick={onClearLogs}
                    className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    <Ban className="h-3 w-3" strokeWidth={1.5} /> Pulisci
                  </button>
                </div>
                {logs.map((l) => (
                  <div key={l.id} className="border-b border-border/30 py-0.5 last:border-0">
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="font-mono text-muted-foreground/60">{l.time}</span>
                      <span className={l.level === "error" ? "font-medium text-danger" : "font-medium text-foreground"}>
                        {l.label}
                      </span>
                    </div>
                    {l.lines.length > 0 && (
                      <pre className="ml-14 whitespace-pre-wrap break-words font-mono text-[10px] text-muted-foreground">
                        {l.lines.join("\n")}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            ))}

          {tab === "linter" &&
            (lintIssues.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">Nessun problema rilevato.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {lintIssues.map((i, idx) => (
                  <li
                    key={idx}
                    className="rounded border border-danger/40 bg-danger/5 px-2 py-1 text-[11px] text-danger"
                  >
                    <span className="font-medium">{KIND_LABEL[i.kind]}</span>{" "}
                    <span className="font-mono opacity-80">{i.where}</span>: {i.error}
                  </li>
                ))}
              </ul>
            ))}

          {tab === "state" && (
            <div className="flex flex-col gap-3 text-[11px]">
              <StateScope title="Stato pagina" token="$page" entries={pageState} hint="si azzera cambiando pagina" />
              <StateScope title="Stato globale" token="$global" entries={globalState} hint="sopravvive alla navigazione" />
              <p className="text-[10px] text-muted-foreground/70">
                Valori di anteprima (iniziali) — diventano live nel portale.
              </p>
            </div>
          )}

          {tab === "refs" && (
            <DataPanel
              queries={queries}
              variables={variables}
              preview={preview}
              user={user}
              role={role}
              itemColumns={itemColumns}
              stateKeys={stateKeys}
              isOverlay={isOverlay}
              inputKeys={inputKeys}
              onInsert={onInsert}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** The last run's result (Response tab) — a query's rows or a JS function's
 *  return, or the error. The query's actual rows are also shown inline in the
 *  Query editor; this mirrors them as the page-wide "last run" (Appsmith). */
const RESPONSE_ROW_CAP = 50;

function ResponseView({ response }: { response: ConsoleResponse }) {
  const kindLabel = response.kind === "query" ? "Query" : "JS";
  return (
    <div className="flex flex-col gap-2 text-[11px]">
      <div className="flex items-center gap-2">
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {kindLabel}
        </span>
        <span className="font-mono font-medium text-foreground">{response.label}</span>
        <span
          className={`text-[10px] ${response.ok ? "text-success" : "text-danger"}`}
        >
          {response.ok
            ? response.kind === "query"
              ? `${response.total} righe`
              : "ok"
            : "errore"}
        </span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground/60">{response.time}</span>
      </div>

      {!response.ok ? (
        <pre className="whitespace-pre-wrap break-words rounded border border-danger/40 bg-danger/5 p-2 font-mono text-[10px] text-danger">
          {response.error}
        </pre>
      ) : response.kind === "js" ? (
        <pre className="whitespace-pre-wrap break-words rounded bg-muted/40 p-2 font-mono text-[10px] text-foreground">
          {response.display}
        </pre>
      ) : response.rows.length === 0 ? (
        <p className="text-muted-foreground">Nessuna riga.</p>
      ) : (
        <pre className="whitespace-pre-wrap break-words rounded bg-muted/40 p-2 font-mono text-[10px] text-muted-foreground">
          {JSON.stringify(response.rows.slice(0, RESPONSE_ROW_CAP), null, 2)}
          {response.rows.length > RESPONSE_ROW_CAP
            ? `\n… +${response.rows.length - RESPONSE_ROW_CAP} righe`
            : ""}
        </pre>
      )}
    </div>
  );
}

/** One scope section in the Stato tab — its declared keys + preview values. */
function StateScope({
  title,
  token,
  entries,
  hint,
}: {
  title: string;
  token: string;
  entries: Record<string, unknown>;
  hint: string;
}) {
  const keys = Object.keys(entries);
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{title}</span>
        <code className="font-mono text-[10px] text-muted-foreground/60">{token}</code>
        <span className="text-[10px] text-muted-foreground/50">· {hint}</span>
      </div>
      {keys.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">Nessuna voce.</p>
      ) : (
        <table className="w-full">
          <tbody>
            {keys.map((k) => (
              <tr key={k} className="border-b border-border/30 last:border-0">
                <td className="py-0.5 pr-3 font-mono text-foreground">{k}</td>
                <td className="py-0.5 font-mono text-muted-foreground">{fmtValue(entries[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function fmtValue(v: unknown): string {
  if (v === undefined) return "—";
  if (v === null) return "null";
  if (typeof v === "object") return Array.isArray(v) ? `[${v.length}]` : "{…}";
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 59)}…` : s;
}
